import { observer } from "mobx-react";
import { useTranslation } from "react-i18next";
import { useEffect } from "react";
import { useSnackbar } from "notistack";
import { SNACKBAR_ESTIMATE_ARCHIVED, SNACKBAR_ESTIMATE_READONLY } from "@utils";
import { useCheckEstimateEditorAccess, useWorkspaceWebsocket } from "@hooks";
import {
  EstimateStructureProvider,
  EditorRoot,
  EstimateAlerts,
  AgentSessionBar,
  useEstimateEditorStore,
  useEstimateComments,
  useHistoryManagerKeybinds,
  useNavigationKeybinds, UnsupportedBrowserAlert
} from "@tools";
import { PdfUploadProgress } from "@components";
import { IconButton } from "@material-ui/core";
import { Close } from "@material-ui/icons";

export const ProjectEditorView = observer(() => {
  const {t} = useTranslation();
  const editorStore = useEstimateEditorStore();
  const commentStore = useEstimateComments();
  const socket = useWorkspaceWebsocket();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const {
    isArchived,
    currentEstimateStructure,
    proposalStep,
    estimateUsers,
    isAiGenerating,
    isAgentWorking,
    unsupportedBrowserAlert,
  } = editorStore;

  const {
    hasEditorPrivileges,
    hasSellerPrivileges,
    isObserver,
    isContractor,
  } = useCheckEstimateEditorAccess(estimateUsers);
  
  useHistoryManagerKeybinds(currentEstimateStructure);
  useNavigationKeybinds(currentEstimateStructure);
  
  useEffect(() => {
    if (isArchived)
      enqueueSnackbar(t("alerts.editor.archive_readonly"), {
        variant: "info",
        key: SNACKBAR_ESTIMATE_ARCHIVED,
        persist: true,
      });
    else {
      closeSnackbar(SNACKBAR_ESTIMATE_ARCHIVED)
    }
  }, [isArchived]);
  
  useEffect(() => {
    if (!isArchived && !isObserver && !isContractor)
      enqueueSnackbar(t("alerts.editor.readonly_info.project"), {
        variant: "info",
        key: SNACKBAR_ESTIMATE_READONLY,
        persist: true,
        preventDuplicate: true,
        action: (snackbarId) => (
          <IconButton
            size="small"
            className="ml-2"
            color="inherit"
            onClick={() => closeSnackbar(snackbarId)}
          >
            <Close />
          </IconButton>
        )
      });
    
  }, [isObserver]);
  
  useEffect(() => {
    commentStore.historyManager.withoutUndo(() => {
      commentStore.setUseInternalComments(!proposalStep)
    })
  }, [proposalStep]);
  
  useEffect(() => {
    commentStore.historyManager.withoutUndo(() => {
      commentStore.setUseExternalComments(hasSellerPrivileges)
    })
  }, [hasSellerPrivileges]);
  
  return (
    <EstimateStructureProvider value={currentEstimateStructure}>
      <EditorRoot
        hasEditorPrivileges={ hasEditorPrivileges && !isArchived }
        hasSellerPrivileges={ hasSellerPrivileges }
        isProposal={ !!proposalStep }
        isPresetEditor={ proposalStep === 1 }
        
        readOnly={ isAiGenerating || isAgentWorking || !socket?.connectionOnline || isArchived || !!proposalStep }
        allowInteractions={ !isAiGenerating && !isAgentWorking && socket?.connectionOnline && proposalStep !== 1 }
        noTurningOff={ !socket?.connectionOnline || proposalStep === 1 || !hasEditorPrivileges }
        
        showStepper={ !isAiGenerating && !isAgentWorking && socket?.connectionOnline && !isArchived && hasSellerPrivileges && hasEditorPrivileges }
        showUserMovement={ proposalStep === 0 }
        showPresetToolbar={ proposalStep === 1 }
        showPdfCover={ proposalStep === 2 }
      />
      <PdfUploadProgress />
      <AgentSessionBar canTakeOver={ hasEditorPrivileges && !isArchived } />
      <EstimateAlerts />
      {
        unsupportedBrowserAlert &&
        <UnsupportedBrowserAlert
          variant="editor"
          onClose={() => editorStore.hideUnsupportedBrowserAlert()}
        />
      }
    </EstimateStructureProvider>
  );
});